import React, { useState, useEffect, useCallback } from 'react';
import {useParams, useNavigate, useLocation} from 'react-router-dom';
import {FormattedMessage, useIntl} from 'react-intl';
import { get, post } from '../../services/api';
import { usePageLoader } from '../../contexts/PageLoaderContext';
import { useNotifier } from '../../contexts/NotificationContext';

import Title from '../../components/common/Title/Title';
import QuestionRenderer from '../../components/QuestionRenderer/QuestionRenderer';
import styles from './PublicFormPage.module.css';
import {hideBackButton, showBackButton} from "../../services/telegramService";
import BrandingFooter from "../../components/common/BrandingFooter/BrandingFooter";
import FullScreenMessage from "../../components/common/FullScreenMessage/FullScreenMessage";
import NotFoundPage from "../NotFoundPage/NotFoundPage";

const PublicFormPage = () => {
    const { slug } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const intl = useIntl();
    const { showPageLoader, hidePageLoader } = usePageLoader();
    const { addNotification } = useNotifier();

    const [form, setForm] = useState(null);
    const [answers, setAnswers] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submissionComplete, setSubmissionComplete] = useState(false);

    const [validationErrors, setValidationErrors] = useState({});
    const [formNotFound, setFormNotFound] = useState(false);

    useEffect(() => {
        hideBackButton();
    }, []);

    useEffect(() => {
        if (location.state?.from === 'settings') {
            showBackButton();
        }
    }, [location.state]);

    useEffect(() => {
        const fetchForm = async () => {
            showPageLoader();
            try {
                const response = await get(`forms/slug/${slug}`);
                if (response.data) {
                    setForm(response.data);
                } else {
                    throw new Error('Form not found');
                }
            } catch (error) {
                setFormNotFound(true);
                // addNotification(error.message || 'Failed to load form', 'error');
                // navigate('/'); // Возвращаем на главную, если форма не найдена
            } finally {
                hidePageLoader();
            }
        };
        fetchForm();
    }, [slug, showPageLoader, hidePageLoader, addNotification, navigate]);

    const handleAnswerChange = useCallback((questionId, value) => {

        if (validationErrors[questionId]) {
            setValidationErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[questionId];
                return newErrors;
            });
        }

        // Находим тип вопроса, чтобы применить специальную логику
        const question = form?.questions.find(q => q.id === questionId);
        let processedValue = value;

        // --- НАЧАЛО НОВОЙ ЛОГИКИ ---
        if (question?.type === 'phone') {
            // Удаляем все, кроме цифр и знака +
            processedValue = value.replace(/[^\d+]/g, '');
        } else if (question?.type === 'integer') {
            // Удаляем все, кроме цифр и знака "-" в начале
            processedValue = value.replace(/[^\d-]/g, '').replace(/(?!^)-/g, '');
        } else if (question?.type === 'decimal') {
            // Удаляем все, кроме цифр, одной точки/запятой и знака "-" в начале
            processedValue = value.replace(/[^,\d.-]/g, '').replace(/(?!^)-/g, '').replace(/([,.])[,.]+/g, '$1');
        }

        setAnswers(prev => ({
            ...prev,
            [questionId]: processedValue,
        }));
    }, [form, validationErrors]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        showPageLoader(intl.formatMessage({ id: "suggestIdeaPage.submitting" }));

        // Подготовка данных для отправки
        const submissionData = {
            answers: Object.entries(answers).map(([questionId, value]) => ({
                question_id: questionId,
                value: value
            }))
        };
        // Бэкенд ожидает { "answers": { "question_id_1": { "value": ... } } }
        // Давайте переделаем под этот формат
        const formattedAnswers = {};
        for (const [questionId, value] of Object.entries(answers)) {
            formattedAnswers[questionId] = { value: value };
        }

        try {
            await post(`forms/${form.id}/submit`, { answers: formattedAnswers });
            setSubmissionComplete(true);

            // "Пример отправки сообщения"
            addNotification(intl.formatMessage({id: "formSettings.submissionMessage.title.placeholder"}), 'success');

        } catch (error) {
            console.log(error)
            if (error.response?.status === 422 && error.response?.data?.errors) {
                const backendErrors = error.response.data.errors;
                const newErrors = {};
                // Преобразуем ошибки от Laravel в удобный для нас формат { question_id: "Текст ошибки" }
                for (const key in backendErrors) {
                    // Ключ приходит в формате "answers.123.value"
                    const questionId = key.split('.')[1];
                    newErrors[questionId] = backendErrors[key][0]; // Берем первое сообщение об ошибке для поля
                }
                setValidationErrors(newErrors);
                // Показываем общее уведомление, что нужно исправить ошибки
                addNotification(intl.formatMessage({ id: "form.validation.summaryError" }), 'error');
            } else {
                // Обрабатываем другие типы ошибок (серверные, сетевые)
                addNotification(error.message || 'Failed to submit form', 'error');
            }
        } finally {
            setIsSubmitting(false);
            hidePageLoader();
        }
    };

    if (formNotFound) {
        return <NotFoundPage />;
    }

    if (!form) {
        // Пока форма загружается, usePageLoader показывает индикатор
        return null;
    }

    if (submissionComplete) {
        const successTitle = form.settings?.submission_message?.title;
        const successText = form.settings?.submission_message?.text;
        return (
            <>
                <FullScreenMessage
                    // emoji="🎉"
                    title={successTitle || intl.formatMessage({ id: "formSettings.submissionMessage.title.placeholder" })}
                    text={successText}
                />
                <BrandingFooter />
            </>
            // <div className={styles.successContainer}>
            //     <Title>{form.settings.submission_message || 'Спасибо!'}</Title>
            //     <p><FormattedMessage id="formSettingsPage.submissionMessage" /></p>
            //     <BrandingFooter />
            // </div>
        );
    }

    return (
        <div>
            <Title>{form.title}</Title>
            {form.description && <p className={styles.description}>{form.description}</p>}

            <form onSubmit={handleSubmit}>
                {form.questions.map(q => (
                    q.type === 'text_block'
                        ? (
                            // Если это текстовый блок, рендерим его как простой div
                            <div key={q.id} className={styles.textBlock}>
                                {q.text}
                            </div>
                        )
                        : (
                    <QuestionRenderer
                        key={q.id}
                        question={q}
                        answer={answers[q.id]}
                        onAnswerChange={handleAnswerChange}
                        error={validationErrors[q.id]}
                    />
                        )
                ))}
                <button type="submit" className={styles.submitButton} disabled={isSubmitting}>
                    {form.settings.submit_button_text || 'Отправить'}
                </button>
            </form>

            <BrandingFooter />

        </div>
    );
};

export default PublicFormPage;
